import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment, { Moment } from 'moment-timezone'
import React, { useEffect, useMemo, useState } from 'react'
import { FocusedInputShape, isInclusivelyBeforeDay } from 'react-dates'
import CalendarDayPickerRange from 'src/components/CalendarDayPickerRange/CalendarDayPickerRange'
import DateTextInputField from 'src/components/DateTextInputField/DateTextInputField'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import { useDropdownOpen } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { css } from 'styled-components'

interface DateRangePickerFieldsDropdownProps {
  id?: string
  since: Moment
  until: Moment
  setSince: (val: Moment | undefined) => void
  setUntil: (val: Moment | undefined) => void
  onProcessDatesChange?: (
    since: Moment | undefined,
    until: Moment | undefined,
  ) => { since: Moment | undefined; until: Moment | undefined }
  onApplyDates?: (since: Moment | undefined, until: Moment | undefined) => void
  onDropdownClose?: () => void
  sinceLabel?: React.ReactElement
  untilLabel?: React.ReactElement
  sinceTooltipText?: React.ReactElement
  untilTooltipText?: React.ReactElement
  tooltipText?: React.ReactElement
  disabled?: boolean | 'since' | 'until'
  loading?: boolean
  initialMonth?: Moment
  isOutsideRange?: (day: moment.Moment) => boolean
  errorMessage?: string | React.ReactElement
  footerContent?: React.ReactElement
  minimumRangeSize?: number
  utc?: boolean
  openLeft?: boolean
  keepDropdownOpen?: boolean
}

const DateRangePickerFieldsDropdown: React.FC<
  DateRangePickerFieldsDropdownProps
> = (props): React.ReactElement => {
  const { dropdownOpen, divRef, handleOpenerClick } = useDropdownOpen({})

  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')

  const disabled = useMemo((): boolean | 'startDate' | 'endDate' => {
    if (props.disabled === true) return true
    if (props.disabled === 'since') return 'startDate'
    if (props.disabled === 'until') return 'endDate'
    return false
  }, [props.disabled])

  const [focusedInput, setFocusedInput] = useState<FocusedInputShape | null>(
    null,
  )

  const momentLocal = props.utc ? moment.utc : moment

  const isOutsideRange = (day: moment.Moment): boolean => {
    if (props.loading === true || props.disabled === true) {
      return true
    } else if (props.isOutsideRange) {
      return props.isOutsideRange(day)
    } else {
      return !isInclusivelyBeforeDay(day, momentLocal())
    }
  }

  const handleStartTextInputChange = (date: string): void => {
    setStartDate(date)
    if (!props.onApplyDates && momentLocal(date, 'll', true).isValid()) {
      props.setSince(momentLocal(date).clone())
    }
  }

  const handleEndTextInputChange = (date: string): void => {
    setEndDate(date)
    if (!props.onApplyDates && momentLocal(date, 'll', true).isValid()) {
      props.setUntil(momentLocal(date))
    }
  }

  useEffect(() => {
    if (!dropdownOpen) props.onDropdownClose?.()
  }, [dropdownOpen])

  // Update fieldDate when filters are externally reset
  useEffect(() => {
    setStartDate(props.since.format('ll'))
    setEndDate(props.until.format('ll'))
  }, [props.since, props.until])

  const onDatesChange = (arg: {
    startDate: Moment | null
    endDate: Moment | null
  }): void => {
    let { startDate: start, endDate: end } = arg

    if (props.onProcessDatesChange) {
      const processed = props.onProcessDatesChange(
        start ?? undefined,
        end ?? undefined,
      )
      start = processed.since ?? null
      end = processed.until ?? null
    }

    setStartDate(start?.format('ll') || '')
    setEndDate(end?.format('ll') || '')

    if (!props.onApplyDates && start && end) {
      props.setSince(start.clone())
      props.setUntil(end.clone())
    }
  }

  const onFocusChange = (focusedInput: FocusedInputShape | null): void => {
    if (!disabled) setFocusedInput(focusedInput)
  }

  return (
    <div
      css={css`
        position: relative;
        width: 100%;
      `}
    >
      <div
        id={props.id}
        ref={divRef}
        onClick={(e): void => {
          if (disabled !== true) {
            handleOpenerClick(e)
          }
        }}
        onKeyPress={(): void => undefined}
        role="button"
        tabIndex={0}
        css={css`
          display: flex;
          align-items: center;
          margin-top: 5px;
          cursor: ${disabled === true ? 'default' : 'pointer'};
          ${getFontCss(
            14,
            disabled === true
              ? styleConstants.colors.gray_darkest
              : themeConstants.colors.cta,
            styleConstants.fontFamilies.regular,
          )};
          justify-content: space-between;
          width: 100%;
        `}
      >
        <DateTextInputField
          label={props.sinceLabel ?? <Trns source={'Start'} />}
          placeholder={''}
          value={startDate}
          focus={focusedInput === 'startDate'}
          onClick={(): void => {
            setFocusedInput('startDate')
          }}
          onChange={(val: string): void => setStartDate(val)}
          onBlur={(val: string): void => handleStartTextInputChange(val)}
          disabled={
            props.loading === true ||
            disabled === true ||
            disabled === 'startDate'
          }
          tooltipText={props.sinceTooltipText}
        />
        <div
          css={css`
            display: flex;
            align-self: flex-end;
            margin-bottom: 12px;
            ${getFontCss(
              14,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          {'-'}
        </div>
        <DateTextInputField
          label={props.untilLabel ?? <Trns source={'End'} />}
          placeholder={''}
          value={endDate}
          focus={focusedInput === 'endDate'}
          onClick={(): void => {
            setFocusedInput('endDate')
          }}
          onChange={(val: string): void => setEndDate(val)}
          onBlur={(val: string): void => handleEndTextInputChange(val)}
          disabled={
            props.loading === true ||
            disabled === true ||
            disabled === 'endDate'
          }
          tooltipText={props.untilTooltipText}
        />
      </div>
      {dropdownOpen && (
        <div
          onClick={(e): void => {
            e.stopPropagation()
          }}
          onKeyDown={(): void => {
            undefined
          }}
          role="button"
          tabIndex={0}
          css={css`
            display: flex;
            flex-direction: column;
            position: absolute;
            ${props.openLeft
              ? css`
                  right: 0px;
                `
              : css`
                  left: 0px;
                `}
            z-index: 2;
            padding-top: 10px;
            border-radius: 10px;
            box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.1);
            background-color: ${styleConstants.colors.white};

            ${getBreakpointDown(breakpointSizes.XXSM)(css`
              left: -30px;
            `)}
          `}
        >
          <CalendarDayPickerRange
            isOutsideRange={isOutsideRange}
            onDatesChange={onDatesChange}
            onFocusChange={onFocusChange}
            focusedInput={focusedInput}
            startDate={startDate}
            endDate={endDate}
            momentLocal={momentLocal}
          />
          {props.errorMessage && (
            <div
              css={css`
                margin-top: 20px;
                ${getFontCss(
                  14,
                  styleConstants.colors.red,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              {props.errorMessage}
            </div>
          )}
          {props.footerContent && (
            <>
              <DividerLineDiv
                css={css`
                  margin: 20px 0 0 0;
                `}
              />
              <div
                css={css`
                  padding-top: 20px;
                `}
              >
                {props.footerContent}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default DateRangePickerFieldsDropdown
